/**
 * **A port of `legacy/frontend/src/screens/PartSegments.tsx`, whole but for its `:1`.** F-016 T8
 * brought `VOICE_PARTS` alone, which the old roster imports from here (`MembersCard.tsx:9`, ours
 * at `:10`); F-017 T5 widened it to the rest — the `voicePartName` import (their `:2`) and the
 * segmented control, the default export (their `:7-57`) — rather than writing a second list.
 *
 * Their `:1` imports `VoicePart` from `../api`. It is a server shape, so here it comes from
 * `@contracts/index` (ADR-0011); `app/src/api.ts` imports the type and does not re-export it.
 * Everything from their `:2` on is theirs byte for byte, and `scripts/ported-partial.txt` holds
 * that region. The precedent is `TimeWheel.tsx` (`FND-049`).
 */
import type { VoicePart } from '@contracts/index';
import { voicePartName } from '../format';

/** The four voices, in the order a choir sits. */
export const VOICE_PARTS: VoicePart[] = ['soprano', 'alto', 'tenor', 'bass'];

/**
 * Which voice somebody sings, as four segments.
 *
 * There were five until 2026-08-24, and the fifth was «—». The owner's
 * decision took it out: a part is not something a person may decline to have,
 * it is the choir's own record of a voice, and the way to have none is to be
 * new — which the roster says in words rather than in a dash. So there is
 * nothing here to press that means «no part», and this control is the regent's
 * alone.
 *
 * Four voices fit on the narrowest phone whole, so nothing is hidden: a wheel
 * would show two of them and a native `<select>` shows none until it is
 * opened, on top of being the one control in the app drawn by the operating
 * system rather than by us.
 *
 * One component and not three copies of one. This field stood in three places
 * — the person's own record, the regent's copy of it, and both onboarding forms
 * — and two of them were a `<select>` while the third was already this. A field
 * that looks like three different fields is what that costs; see the addendum
 * of 2026-08-21, which asked for all three at once for exactly that reason.
 *
 * The tray it sits in takes its colour from the plane it stands on, the way
 * every other field does: `--surface` on the page and on a sheet, `--bg` inside
 * a card.
 */
export default function PartSegments({
  value,
  busy,
  onPick,
}: {
  /** Null is «not given yet», which this control shows and cannot produce. */
  value: VoicePart | null;
  busy?: boolean;
  onPick: (part: VoicePart) => void;
}) {
  return (
    <div className="parts">
      {VOICE_PARTS.map((part) => (
        <button
          key={part}
          className={part === value ? 'part part--picked' : 'part'}
          aria-pressed={part === value}
          disabled={busy}
          onClick={() => onPick(part)}
        >
          {voicePartName(part)}
        </button>
      ))}
    </div>
  );
}
